import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  FiArrowLeft, FiClock, FiAlertCircle, FiDollarSign,
  FiCalendar, FiMapPin, FiPackage, FiTruck,
  FiCamera, FiX, FiCheckCircle, FiChevronLeft, FiChevronRight,
  FiSmartphone, FiMail, FiCreditCard, FiBox, FiFileText,
} from 'react-icons/fi';
import { getImagenUrl } from 'shared/api';
import useWindowSize from 'shared/hooks/useWindowSize';
import { fmtMonto } from 'shared/format';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const L = {
  retiro: 'Retiro', delivery: 'Delivery', encomienda: 'Encomienda',
  pendiente: 'Pendiente', en_revision: 'En revision', aprobado: 'Aprobado',
  esperando_pago: 'Pago pendiente', pago_en_verificacion: 'Verificando',
  pagado: 'Pagado', armado: 'Armando', listo: 'Listo', despachado: 'Despachado',
  en_camino: 'En camino', llegada: 'Llegada', entregado: 'Entregado',
  credito_pagado: 'Credito pagado',
};

const METODO_ICON = { pago_movil: FiSmartphone, zelle: FiMail, paypal: FiDollarSign, binance: FiDollarSign, nequi: FiSmartphone, transferencia: FiCreditCard, efectivo_usd: FiDollarSign, efectivo_bs: FiDollarSign, efectivo_pesos: FiDollarSign };

const ESTADO_ERP = {
  'POR COBRAR': 'Por cobrar', 'ABONADA': 'Abonada', 'COBRADA': 'Cobrada',
  'DEVUELTA T': 'Devuelta', 'DEVUESLA T': 'Devuelta',
};

function fmt(d) { if (!d) return '--'; const dt = new Date(d); if (isNaN(dt.getTime())) return '--'; return dt.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' }); }
function fmtLong(d) { if (!d) return null; const dt = new Date(d); if (isNaN(dt.getTime())) return null; return dt.toLocaleDateString('es-VE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); }

/* ══════════════════════════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════════════════════════ */
export default function ClienteCreditos({ sessionId, schemaName, companySlug, onBack }) {
  const [creditos, setCreditos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cargandoCuenta, setCargandoCuenta] = useState(true);
  const [sel, setSel] = useState(null);
  const [abonos, setAbonos] = useState([]);
  const [cuenta, setCuenta] = useState(null);
  const [facturaSel, setFacturaSel] = useState(null);
  const [filtro, setFiltro] = useState('todas'); // 'todas' | 'deudas' | 'completadas'

  useEffect(() => {
    if (!sessionId) return;
    setCargando(true);
    axios.get(`${API_URL}/credito/mis-creditos/${schemaName}/${sessionId}`, {
      headers: { 'x-empresa-slug': (schemaName || '').replace(/_/g, '-') },
    }).then(r => { if (r.data.status === 'success') setCreditos(r.data.data || []); })
      .finally(() => setCargando(false));
  }, [sessionId, schemaName]);

  useEffect(() => {
    if (!sessionId) return;
    setCargandoCuenta(true);
    axios.get(`${API_URL}/credito/estado-cuenta/${schemaName}/${sessionId}`, {
      headers: { 'x-empresa-slug': (schemaName || '').replace(/_/g, '-') },
    }).then(r => { if (r.data.status === 'success') setCuenta(r.data.data || null); })
      .catch(() => setCuenta(null))
      .finally(() => setCargandoCuenta(false));
  }, [sessionId, schemaName]);

  const abrirDetalle = async (c) => {
    setSel(c);
    try {
      const slug = (schemaName || '').replace(/_/g, '-');
      const r = await axios.get(`${API_URL}/credito/pedido/${c.id}/abonos`, {
        headers: { 'x-empresa-slug': slug, 'x-user-cedula': '' },
      });
      if (r.data.status === 'success') setAbonos(r.data.data || []);
    } catch { setAbonos([]); }
  };

  // ── Derivados ──
  const esErp = !!(cuenta && cuenta.disponible);
  const resumen = cuenta?.resumen || {};
  const facturas = (cuenta?.movimientos || []).filter(m => m.origen === 'erp');

  const totPen = creditos.reduce((s, c) => s + (c.faltante || 0), 0);
  const totAbo = creditos.reduce((s, c) => s + (c.abonado || 0), 0);
  const totCre = creditos.reduce((s, c) => s + (c.total || 0), 0);

  // Saldo principal (autoritativo): ERP si está enlazado, sino nativo.
  const saldoPendiente = esErp ? (resumen.saldo_abierto_usd ?? 0) : totPen;
  const limite = esErp ? resumen.limite_credito_usd : totCre;
  const disponible = esErp
    ? resumen.credito_disponible_usd
    : (totCre - totPen);

  // Timeline unificado: facturas ERP + pedidos nativos, ordenado por fecha desc.
  // Deduplicación: si una factura ERP concuerda con un pedido nativo (mismo total y misma fecha),
  // se descarta la factura y se conserva el pedido (registro más rico: artículos, abonos, sede).
  const timeline = useMemo(() => {
    const sameAmount = (a, b) => Math.abs((a || 0) - (b || 0)) < 0.02;
    const sameDay = (a, b) => a && b && String(a).slice(0, 10) === String(b).slice(0, 10);

    const items = [];

    // Primero pedidos nativos (tienen prioridad).
    creditos.forEach(c => {
      items.push({ tipo: 'pedido', ts: c.fechapedido, c });
    });

    // Luego facturas ERP, descartando las que concuerdan con un pedido.
    facturas.forEach(m => {
      const duplicada = creditos.some(c =>
        sameAmount(m.total_usd, c.total) && sameDay(m.fecha, c.fechapedido)
      );
      if (duplicada) return;
      items.push({ tipo: 'factura', ts: m.fecha, m });
    });

    items.sort((a, b) => {
      const ta = a.ts ? new Date(a.ts).getTime() : 0;
      const tb = b.ts ? new Date(b.ts).getTime() : 0;
      return tb - ta;
    });
    return items;
  }, [facturas, creditos]);

  // Timeline filtrado por deudas / completadas.
  const esDeuda = (item) => item.tipo === 'pedido' ? (item.c.faltante > 0) : ((item.m.saldo_usd ?? 0) > 0);
  const timelineFiltrado = useMemo(() => {
    if (filtro === 'todas') return timeline;
    return timeline.filter(it => filtro === 'deudas' ? esDeuda(it) : !esDeuda(it));
  }, [timeline, filtro]);

  const totalDeudas = timeline.filter(esDeuda).length;
  const totalCompletadas = timeline.filter(it => !esDeuda(it)).length;

  if (cargando) return <Loading onBack={onBack} />;
  if (sel) return <DetailScreen sel={sel} abonos={abonos} onClose={() => setSel(null)} schemaName={schemaName} onRefresh={() => abrirDetalle(sel)} />;
  if (facturaSel) return <FacturaScreen factura={facturaSel} onClose={() => setFacturaSel(null)} />;

  const vacio = creditos.length === 0 && facturas.length === 0;

  return (
    <div style={css.wrap}>
      <div style={css.scroll}>
        {/* Hero */}
        <Hero
          esErp={esErp}
          nombre={esErp ? resumen.nombre_erp : null}
          saldoPendiente={saldoPendiente}
          limite={limite}
          disponible={disponible}
          abonado={totAbo}
          vencidas={esErp ? (resumen.vencidas_usd ?? 0) : null}
          vencidasCount={esErp ? (resumen.vencidas_count ?? 0) : null}
          cargando={cargandoCuenta}
        />

        {vacio ? (
          <Empty />
        ) : (
          <>
            {/* Resumen de crédito nativo (solo si no hay ERP, para no duplicar métricas) */}
            {!esErp && (
              <div style={css.summary}>
                <MCard icon={<FiDollarSign size={14} />} label="Total creditos" val={`$${fmtMonto(totCre)}`} color="var(--color-primary)" />
                <MCard icon={<FiAlertCircle size={14} />} label="Pendiente" val={`$${fmtMonto(totPen)}`} color={totPen > 0 ? '#ea4335' : 'var(--color-success)'} />
                <MCard icon={<FiCheckCircle size={14} />} label="Abonado" val={`$${fmtMonto(totAbo)}`} color="var(--color-success)" />
              </div>
            )}

            {/* Filtro de historial */}
            <FiltroHistorial filtro={filtro} setFiltro={setFiltro} totalDeudas={totalDeudas} totalCompletadas={totalCompletadas} />

            {/* Historial unificado (una sola vez) */}
            <Label>Historial ({timelineFiltrado.length})</Label>
            <div style={{ padding: '0 12px' }}>
              {timelineFiltrado.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 0', fontSize: 13, color: 'var(--color-text-muted)' }}>
                  No hay {filtro === 'deudas' ? 'deudas pendientes' : filtro === 'completadas' ? 'pagos completados' : 'registros'}.
                </div>
              ) : (
                timelineFiltrado.map((item, i) =>
                  item.tipo === 'factura'
                    ? <CreditoItem key={`f-${item.m.referencia}-${i}`} item={movToItem(item.m)} onClick={() => setFacturaSel(item.m)} />
                    : <CreditoItem key={`p-${item.c.id}`} item={pedidoToItem(item.c)} onClick={() => abrirDetalle(item.c)} />
                )
              )}
            </div>
            <div style={{ height: 80 }} />
          </>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   HERO
   ══════════════════════════════════════════════════════════════════ */
function Hero({ esErp, nombre, saldoPendiente, limite, disponible, abonado, vencidas, vencidasCount, cargando }) {
  const vencido = (vencidas ?? 0) > 0;
  const hayLimite = limite !== null && limite !== undefined;
  const hayDisponible = disponible !== null && disponible !== undefined;

  return (
    <div style={css.hero}>
      <div style={css.heroTop}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={css.heroIc}><FiCreditCard size={20} color="#fff" /></div>
          <div>
            <div style={css.heroTitle}>{esErp ? 'Estado de cuenta' : 'Mis créditos'}</div>
            {esErp && nombre && <div style={css.heroSub}>{nombre}</div>}
          </div>
        </div>
        {esErp && vencido && (
          <span style={css.heroVencido}><FiAlertCircle size={12} /> {vencidasCount} vencida{vencidasCount === 1 ? '' : 's'}</span>
        )}
      </div>

      <div style={css.heroAmountWrap}>
        <span style={css.heroAmountLabel}>Saldo pendiente</span>
        {cargando ? (
          <span style={css.heroSkeleton} />
        ) : (
          <span style={{ ...css.heroAmount, color: saldoPendiente > 0 ? '#fff' : 'rgba(255,255,255,0.9)' }}>
            ${fmtMonto(saldoPendiente ?? 0)}
          </span>
        )}
        {vencido && !cargando && <span style={css.heroVencidoMonto}>Vencido: ${fmtMonto(vencidas ?? 0)}</span>}
      </div>

      <div style={css.heroStats}>
        {cargando ? (
          <>
            <div style={css.heroStat}><span style={css.heroSkeleton} /></div>
            <div style={css.heroStat}><span style={css.heroSkeleton} /></div>
          </>
        ) : (
          <>
            {hayLimite && (
              <div style={css.heroStat}>
                <span style={css.heroStatLabel}>Límite de crédito</span>
                <span style={css.heroStatVal}>${fmtMonto(limite ?? 0)}</span>
              </div>
            )}
            {hayDisponible && (
              <div style={css.heroStat}>
                <span style={css.heroStatLabel}>Disponible</span>
                <span style={css.heroStatVal}>${fmtMonto(disponible ?? 0)}</span>
              </div>
            )}
            {!esErp && (
              <div style={css.heroStat}>
                <span style={css.heroStatLabel}>Abonado</span>
                <span style={css.heroStatVal}>${fmtMonto(abonado ?? 0)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FiltroHistorial({ filtro, setFiltro, totalDeudas, totalCompletadas }) {
  const opts = [
    { id: 'todas', label: 'Todo' },
    { id: 'deudas', label: `Deudas (${totalDeudas})` },
    { id: 'completadas', label: `Completados (${totalCompletadas})` },
  ];
  return (
    <div style={css.filtroWrap}>
      {opts.map(o => (
        <button
          key={o.id}
          onClick={() => setFiltro(o.id)}
          style={{
            ...css.filtroBtn,
            background: filtro === o.id ? 'var(--color-primary)' : 'var(--color-surface)',
            color: filtro === o.id ? '#fff' : 'var(--color-text)',
            borderColor: filtro === o.id ? 'var(--color-primary)' : 'var(--color-border)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   TIMELINE ITEMS
   ══════════════════════════════════════════════════════════════════ */
function dotColor(estado) {
  if (estado === 'POR COBRAR') return '#ea4335';
  if (estado === 'ABONADA') return '#f9a825';
  if (estado === 'COBRADA') return '#34a853';
  return '#9aa0a6';
}

/* Normalizadores: factura ERP y pedido nativo comparten la misma tarjeta. */
function movToItem(m) {
  return {
    tipo: 'factura',
    ref: m.referencia,
    total: m.total_usd ?? 0,
    abonado: m.abonado_usd ?? 0,
    faltante: m.saldo_usd ?? 0,
    fecha: m.fecha || null,
    vence: m.vence || null,
    tipo_doc: m.tipo_doc || '',
    productos: Array.isArray(m.productos) ? m.productos : [],
  };
}

function pedidoToItem(c) {
  return {
    tipo: 'pedido',
    ref: String(c.id),
    total: c.total ?? 0,
    abonado: c.abonado ?? 0,
    faltante: c.faltante ?? 0,
    fecha: c.fechapedido || null,
    vence: c.fecha_limite_pago || null,
    tipo_doc: '',
    sede: c.sede_nombre || '',
    productos: Array.isArray(c.productos) ? c.productos : [],
  };
}

function CreditoItem({ item, onClick }) {
  const esErp = item.tipo === 'factura';
  const total = Number(item.total || 0);
  const abonado = Number(item.abonado || 0);
  const faltante = Number(item.faltante || 0);
  const v = faltante > 0 && item.vence && new Date(item.vence) < new Date();
  const ep = faltante > 0;
  const pagado = faltante <= 0;
  const prods = Array.isArray(item.productos) ? item.productos : [];
  const color = ep ? (v ? '#ea4335' : '#f9a825') : '#34a853';

  return (
    <div style={css.tl}>
      <div style={{ ...css.tlDot, background: color }} />
      <div onClick={onClick} style={css.tlCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={css.tlTitle}>{esErp ? `Factura ${item.ref}` : `Pedido #${item.ref}`}</span>
              {esErp && (
                <span style={{ padding: '2px 6px', borderRadius: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', background: 'var(--color-bg)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
                  ADVIN
                </span>
              )}
              <Badge color={color} bg={color === '#ea4335' ? '#fce8e6' : color === '#f9a825' ? '#fff8e1' : '#e6f4ea'}>
                {ep ? (v ? 'Vencido' : 'Pendiente') : 'Pagado'}
              </Badge>
            </div>
            {prods.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {prods.slice(0, 3).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, background: 'var(--color-bg)', fontSize: 10, color: 'var(--color-text-muted)' }}>
                    {p.imagen_url ? <img src={getImagenUrl(p.imagen_url)} alt="" style={{ width: 14, height: 14, borderRadius: 3, objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none'; }} /> : <FiPackage size={10} />}
                    <span style={{ maxWidth: 80, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</span>
                    <span style={{ fontWeight: 600 }}>x{p.cantidad}</span>
                  </div>
                ))}
                {prods.length > 3 && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>+{prods.length - 3} más</span>}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiCalendar size={10} />{fmt(item.fecha)}</span>
              {item.vence && <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: v ? '#ea4335' : undefined }}><FiClock size={10} />Vence {fmt(item.vence)}</span>}
              {esErp && item.tipo_doc && <span>{item.tipo_doc}</span>}
              {!esErp && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><FiMapPin size={10} />{item.sede || '-'}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-primary)' }}>${fmtMonto(total)}</div>
            {ep && <div style={{ fontSize: 10, color: '#ea4335', marginTop: 2, fontWeight: 600 }}>Falta ${fmtMonto(faltante)}</div>}
          </div>
          <FiChevronRight size={14} color="var(--color-text-muted)" style={{ marginTop: 2, marginLeft: 4 }} />
        </div>

        {/* barra de progreso abonado/total */}
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'var(--color-text-muted)', marginBottom: 3 }}>
            <span>${fmtMonto(abonado)} abonado</span>
            <span>${fmtMonto(total)} total</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--color-border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 2, background: pagado ? 'var(--color-success)' : color, width: `${Math.min(100, (abonado / (total || 1)) * 100)}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FACTURA DETALLE
   ══════════════════════════════════════════════════════════════════ */
function FacturaScreen({ factura, onClose }) {
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const color = dotColor(factura.estado);
  const saldo = factura.saldo_usd ?? 0;
  const rows = [
    ['Referencia', factura.referencia],
    ['Tipo', factura.tipo_doc || '--'],
    ['Estado', ESTADO_ERP[factura.estado] || factura.estado || '--'],
    ['Fecha', fmtLong(factura.fecha) || fmt(factura.fecha)],
    ['Vencimiento', factura.vence ? fmtLong(factura.vence) : '--'],
  ];

  const content = (
    <div style={{ padding: '16px', paddingBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FiFileText size={22} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-primary)' }}>Factura {factura.referencia}</div>
          <Badge color={color} bg={`${color}14`}>{ESTADO_ERP[factura.estado] || factura.estado}</Badge>
        </div>
      </div>

      <div style={css.factGrid}>
        {rows.map(([k, v]) => (
          <div key={k} style={css.factCell}>
            <span style={css.factLbl}>{k}</span>
            <span style={css.factVal}>{v}</span>
          </div>
        ))}
      </div>

      <div style={css.factMontos}>
        <div style={css.factMonto}>
          <span style={css.factMontoL}>Total</span>
          <span style={css.factMontoV}>${fmtMonto(factura.total_usd ?? 0)}</span>
        </div>
        <div style={css.factMonto}>
          <span style={css.factMontoL}>Abonado</span>
          <span style={{ ...css.factMontoV, color: 'var(--color-success)' }}>${fmtMonto(factura.abonado_usd ?? 0)}</span>
        </div>
        <div style={{ ...css.factMonto, background: saldo > 0 ? '#fef2f2' : '#f0fdf4' }}>
          <span style={css.factMontoL}>Saldo</span>
          <span style={{ ...css.factMontoV, color: saldo > 0 ? '#ea4335' : 'var(--color-success)' }}>${fmtMonto(saldo)}</span>
        </div>
      </div>

      {Array.isArray(factura.productos) && factura.productos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={css.sectionH}>Artículos ({factura.productos.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {factura.productos.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 14, background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--color-bg)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.imagen_url ? (
                    <img src={getImagenUrl(p.imagen_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <FiPackage size={18} color="var(--color-text-muted)" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</div>
                  {p.codigo && <div style={{ fontSize: 10, color: 'var(--color-text-muted)', fontFamily: 'monospace', marginTop: 2 }}>{p.codigo}</div>}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>x{p.cantidad}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-primary)' }}>${fmtMonto(p.subtotal ?? 0)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {saldo > 0 && factura.vence && new Date(factura.vence) < new Date() && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <FiAlertCircle size={16} color="#ea4335" style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ fontSize: 12, color: '#ea4335', lineHeight: 1.5 }}>Esta factura está vencida. Contacta a la empresa para regularizar tu cuenta.</div>
        </div>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <div style={css.modalOverlay} onClick={onClose}>
        <div style={css.modal} onClick={e => e.stopPropagation()}>
          <div style={css.modalHead}>
            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-primary)' }}>Detalle de factura</span>
            <button onClick={onClose} style={css.closeBtn}><FiX size={18} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={css.fsWrap}>
      <div style={css.fsHead}>
        <button onClick={onClose} style={css.bb}><FiArrowLeft size={18} /></button>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-primary)' }}>Detalle de factura</span>
        <div style={{ width: 36 }} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>{content}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   DETAIL (PEDIDO NATIVO)
   ══════════════════════════════════════════════════════════════════ */
function DetailScreen({ sel, abonos, onClose, schemaName, onRefresh }) {
  const { width } = useWindowSize();
  const isDesktop = width >= 768;
  const [paso, setPaso] = useState('resumen');
  const [monto, setMonto] = useState('');
  const [metodoPago, setMetodoPago] = useState(null);
  const [metodos, setMetodos] = useState([]);
  const [compFile, setCompFile] = useState(null);
  const [compPreview, setCompPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [prodPopup, setProdPopup] = useState(null);
  const fileRef = useRef(null);

  const v = sel.faltante > 0 && sel.fecha_limite_pago && new Date(sel.fecha_limite_pago) < new Date();
  const ep = sel.faltante > 0;
  const prods = Array.isArray(sel.productos) ? sel.productos : [];
  const abonosOk = abonos.filter(a => a.estado !== 'rechazado');

  const openAbonar = () => {
    setPaso('abonar');
    setMonto(sel.faltante.toFixed(2));
    setMetodoPago(null);
    setCompFile(null); setCompPreview(null);
    const slug = (schemaName || '').replace(/_/g, '-');
    axios.get(`${API_URL}/pedidos/${sel.id}/metodos-pago`, { headers: { 'x-empresa-slug': slug } })
      .then(r => { if (r.data.status === 'success') setMetodos(r.data.data || []); }).catch(() => {});
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setCompFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setCompPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const enviarAbono = async () => {
    const m = parseFloat(monto);
    if (!m || m <= 0 || m > (sel?.faltante || 0)) return;
    setEnviando(true);
    try {
      const slug = (schemaName || '').replace(/_/g, '-');
      const fd = new FormData();
      fd.append('pedido_id', sel.id); fd.append('schema_name', schemaName);
      fd.append('monto', m); fd.append('metodo_pago', metodoPago?.nombre || metodoPago?.codigo || 'Abono');
      if (compFile) fd.append('comprobante', compFile);
      await axios.post(`${API_URL}/credito/abonar-cliente`, fd, { headers: { 'Content-Type': 'multipart/form-data', 'x-empresa-slug': slug } });
      setPaso('resumen'); onRefresh();
    } catch (e) { alert(e?.response?.data?.detail || 'Error al enviar'); }
    setEnviando(false);
  };

  const content = paso === 'abonar' ? (
    <AbonarForm sel={sel} monto={monto} setMonto={setMonto} metodos={metodos} metodoPago={metodoPago}
      setMetodoPago={setMetodoPago} compPreview={compPreview} compFile={compFile} fileRef={fileRef}
      handleFile={handleFile} setCompFile={setCompFile} setCompPreview={setCompPreview}
      onBack={() => setPaso('resumen')} enviarAbono={enviarAbono} enviando={enviando} isDesktop={isDesktop} />
  ) : (
    <Resumen sel={sel} abonosOk={abonosOk} prods={prods} ep={ep} v={v}
      onAbonar={openAbonar} onProdClick={setProdPopup} isDesktop={isDesktop} />
  );

  if (isDesktop) {
    return (
      <div style={css.modalOverlay} onClick={onClose}>
        <div style={css.modal} onClick={e => e.stopPropagation()}>
          <div style={css.modalHead}>
            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-primary)' }}>
              {paso === 'abonar' ? 'Registrar abono' : `Pedido #${sel.id}`}
            </span>
            <button onClick={paso === 'abonar' ? () => setPaso('resumen') : onClose} style={css.closeBtn}><FiX size={18} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>{content}</div>
        </div>
        {prodPopup && <ProdPopup prod={prodPopup} schemaName={schemaName} onClose={() => setProdPopup(null)} />}
      </div>
    );
  }

  return (
    <div style={css.fsWrap}>
      <div style={css.fsHead}>
        <button onClick={paso === 'abonar' ? () => setPaso('resumen') : onClose} style={css.bb}><FiArrowLeft size={18} /></button>
        <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-primary)' }}>{paso === 'abonar' ? 'Abonar' : `Pedido #${sel.id}`}</span>
        <div style={{ width: 36 }} />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>{content}</div>
      {prodPopup && <ProdPopup prod={prodPopup} schemaName={schemaName} onClose={() => setProdPopup(null)} />}
    </div>
  );
}

function Resumen({ sel, abonosOk, prods, ep, v, onAbonar, onProdClick, isDesktop }) {
  return (
    <div>
      <div style={{ padding: '4px 16px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Pill icon={<FiCalendar size={11} />} label="Pedido" val={fmt(sel.fechapedido)} />
        <Pill icon={<FiTruck size={11} />} label="Entrega" val={L[sel.tipo_entrega] || '--'} />
        <Pill icon={<FiBox size={11} />} label="Estado" val={L[sel.estado] || sel.estado} />
        <Pill icon={<FiClock size={11} />} label="Limite" val={fmt(sel.fecha_limite_pago)} accent={v ? '#ea4335' : '#f9a825'} />
      </div>

      <div style={css.cardRow}>
        <div style={css.dCard}><span style={css.dCardL}>Total</span><span style={{ ...css.dCardV, color: 'var(--color-primary)' }}>${fmtMonto(sel.total)}</span></div>
        <div style={css.dCard}><span style={css.dCardL}>Abonado</span><span style={{ ...css.dCardV, color: 'var(--color-success)' }}>${fmtMonto(sel.abonado)}</span></div>
        <div style={{ ...css.dCard, background: ep ? '#fef2f2' : '#f0fdf4' }}>
          <span style={css.dCardL}>Faltante</span>
          <span style={{ ...css.dCardV, color: ep ? '#ea4335' : 'var(--color-success)' }}>${fmtMonto(sel.faltante)}</span>
        </div>
      </div>

      {sel.fecha_limite_pago && (
        <div style={{ margin: '0 16px 12px', padding: '12px 16px', borderRadius: 14, fontSize: 13, background: v ? '#fef2f2' : '#f0fdf4', border: `1px solid ${v ? '#fecaca' : '#bbf7d0'}`, display: 'flex', gap: 10 }}>
          <FiAlertCircle size={16} style={{ color: v ? '#ea4335' : 'var(--color-success)', marginTop: 1, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, color: v ? '#ea4335' : 'var(--color-success)' }}>{v ? 'Credito vencido' : 'Fecha limite de pago'}</div>
            <div style={{ color: 'var(--color-text)', marginTop: 2 }}>{fmtLong(sel.fecha_limite_pago)}</div>
            {v && <div style={{ color: '#ea4335', marginTop: 4, fontSize: 11 }}>Realiza tu pago lo antes posible para evitar recargos.</div>}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={css.sectionH}>Productos ({prods.length})</div>
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {prods.map((p, i) => (
            <div key={i} onClick={() => onProdClick(p)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, borderRadius: 14, background: 'var(--color-surface)', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--color-bg)', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {p.imagen_url ? <img src={getImagenUrl(p.imagen_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} /> : <FiPackage size={20} color="var(--color-text-muted)" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>x{p.cantidad} · ${fmtMonto(p.subtotal || 0)}</div>
              </div>
              <FiChevronRight size={14} color="var(--color-text-muted)" />
            </div>
          ))}
        </div>
      </div>

      {abonosOk.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={css.sectionH}>Historial de abonos ({abonosOk.length})</div>
          <div style={{ padding: '0 16px' }}>
            {abonosOk.map((a, i) => (
              <div key={a.id || i} style={{ display: 'flex', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-success)', marginTop: 6 }} />
                  {i < abonosOk.length - 1 && <div style={{ width: 2, flex: 1, background: 'var(--color-border)' }} />}
                </div>
                <div style={{ flex: 1, paddingBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {a.metodo_pago || 'Abono'}
                        {a.estado === 'pendiente' && <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 9, background: '#fef3c7', color: '#d97706' }}>Pendiente</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{new Date(a.fecha).toLocaleDateString()} · {a.registrado_nombre || 'Cliente'}</div>
                      {a.comprobante_url && <a href={a.comprobante_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--color-secondary)', textDecoration: 'underline' }}>Ver comprobante</a>}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-success)' }}>+${fmtMonto(a.monto)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {ep && (
        <div style={{ padding: '8px 16px 16px' }}>
          <button onClick={onAbonar} style={{ ...css.footBtn, width: '100%' }}>
            <FiDollarSign size={18} /> Realizar abono
          </button>
        </div>
      )}
    </div>
  );
}

function AbonarForm({ sel, monto, setMonto, metodos, metodoPago, setMetodoPago, compPreview, compFile, fileRef, handleFile, setCompFile, setCompPreview, onBack, enviarAbono, enviando, isDesktop }) {
  const faltante = sel.faltante || 0;
  const presets = [{ l: 'Mitad', v: (faltante / 2).toFixed(2) }, { l: 'Completo', v: faltante.toFixed(2) }];

  return (
    <div style={{ padding: '16px', paddingBottom: 60 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 16 }}>Registrar abono</div>

      <div style={{ marginBottom: 20 }}>
        <div style={css.inpLabel}>Monto a abonar</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>Faltante: <strong style={{ color: '#ea4335' }}>${fmtMonto(faltante)}</strong></div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'var(--color-surface)', border: '2px solid var(--color-secondary)', borderRadius: 16, padding: '4px 4px 4px 18px', marginBottom: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-muted)', marginRight: 4 }}>$</span>
          <input type="number" value={monto} onChange={e => setMonto(e.target.value)} min="0.01" max={faltante} step="0.01"
            style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 20, fontWeight: 700, color: 'var(--color-text)', outline: 'none', fontFamily: 'inherit', padding: '8px 0' }}
            placeholder="0.00" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {presets.map(p => {
            const active = parseFloat(monto) === parseFloat(p.v);
            return (
              <button key={p.l} onClick={() => setMonto(p.v)} style={{
                flex: 1, padding: '10px', borderRadius: 12, border: active ? '2px solid var(--color-secondary)' : '1px solid var(--color-border)',
                background: active ? 'var(--color-bg-hover)' : 'var(--color-surface)', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <div style={{ fontSize: 11, color: active ? 'var(--color-secondary)' : 'var(--color-text-muted)', fontWeight: 600 }}>{p.l}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: active ? 'var(--color-secondary)' : 'var(--color-text)' }}>${p.v}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={css.inpLabel}>Metodo de pago</div>
        {metodos.length === 0 ? (
          <div style={{ padding: 12, borderRadius: 12, background: 'var(--color-bg)', textAlign: 'center', fontSize: 12, color: 'var(--color-text-muted)' }}>Cargando metodos...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {metodos.map(m => {
              const Icon = METODO_ICON[m.codigo] || FiCreditCard;
              const sel = metodoPago?.id === m.id;
              return (
                <button key={m.id} onClick={() => setMetodoPago(m)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  border: sel ? '2px solid var(--color-secondary)' : '1px solid var(--color-border)',
                  background: sel ? 'var(--color-bg-hover)' : 'var(--color-surface)',
                }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: sel ? 'var(--color-secondary)' : 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sel ? '#fff' : 'var(--color-text-muted)', flexShrink: 0 }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: sel ? 'var(--color-secondary)' : 'var(--color-text)' }}>{m.nombre}</div>
                  {sel && <FiCheckCircle size={18} color="var(--color-secondary)" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={css.inpLabel}>Comprobante</div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>Opcional — PNG, JPG o PDF</div>
        <input type="file" accept="image/*,.pdf" ref={fileRef} onChange={handleFile} style={{ display: 'none' }} />
        {compPreview ? (
          <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            <img src={compPreview} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }} />
            <button onClick={() => { setCompFile(null); setCompPreview(null); }} style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiX size={14} />
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} style={{ width: '100%', padding: '20px', borderRadius: 14, border: '2px dashed var(--color-border)', background: 'var(--color-bg)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <FiCamera size={24} color="var(--color-text-muted)" />
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Subir comprobante</span>
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onBack} style={css.btnSec}>Cancelar</button>
        <button onClick={enviarAbono} disabled={enviando || !monto || parseFloat(monto) <= 0}
          style={{ ...css.btnPri, opacity: enviando || !monto ? 0.5 : 1 }}>
          {enviando ? 'Enviando...' : 'Confirmar'}
        </button>
      </div>
    </div>
  );
}

function ProdPopup({ prod, schemaName, onClose }) {
  const [data, setData] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (!prod?.codigo) return;
    const slug = (schemaName || '').replace(/_/g, '-');
    axios.get(`${API_URL}/catalogo/producto/${prod.codigo}?schema_name=${schemaName}`, { headers: { 'x-empresa-slug': slug } })
      .then(r => { if (r.data.status === 'success') setData(r.data.data); }).catch(() => {});
  }, [prod?.codigo, schemaName]);

  const d = data || prod;
  const imagenes = d.imagenes || (d.imagen_url ? [{ url: d.imagen_url }] : []);
  const imgActual = imagenes[imgIdx];

  return (
    <div onClick={onClose} style={css.popOverlay}>
      <div onClick={e => e.stopPropagation()} style={css.popCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-primary)' }}>{d.nombre || d.descripcion || prod.nombre}</span>
            {d.marca && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{d.marca}</div>}
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', flexShrink: 0 }}><FiX size={14} /></button>
        </div>

        {imagenes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: 'var(--color-bg)' }}>
              <img key={imgIdx} src={getImagenUrl(imgActual.url)} alt={d.nombre} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
              {imagenes.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(prev => (prev - 1 + imagenes.length) % imagenes.length)} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiChevronLeft size={16} /></button>
                  <button onClick={() => setImgIdx(prev => (prev + 1) % imagenes.length)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.5)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiChevronRight size={16} /></button>
                  <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4 }}>
                    {imagenes.map((_, i) => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === imgIdx ? '#fff' : 'rgba(255,255,255,0.4)' }} />)}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
          {[
            ['Codigo', d.codigo || prod.codigo], ['Cantidad', prod.cantidad],
            ['Precio', `$${fmtMonto(d.precio || prod.precio || 0)}`], ['Subtotal', `$${fmtMonto(prod.subtotal || 0)}`],
            ['Pesable', (d.pesable || prod.pesable) ? 'Si' : 'No'], ['Depto', d.departamento || '--'],
            ['Grupo', d.grupo || '--'], ['Procede', d.procede || '--'],
            ['Ref', d.referencia || '--'],
          ].map(([k, v]) => (
            <div key={k} style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 9, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{k}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>{v || '--'}</div>
            </div>
          ))}
        </div>

        {d.descripcion_completa && (
          <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'var(--color-bg)' }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Descripcion</div>
            <div style={{ fontSize: 12, color: 'var(--color-text)', lineHeight: 1.5 }}>{d.descripcion_completa}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PRIMITIVOS
   ══════════════════════════════════════════════════════════════════ */
function Spinner() { return <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid var(--color-border)', borderTop: '3px solid var(--color-secondary)', animation: 'spin 0.8s linear infinite' }} />; }
function Loading({ onBack }) { return <div style={css.wrap}><div style={css.cent}><Spinner /></div></div>; }

function Empty() {
  return (
    <div style={css.empty}>
      <div style={css.emptyIc}><FiDollarSign size={28} color="var(--color-text-muted)" /></div>
      <div style={css.emptyT}>Sin historial de crédito</div>
      <div style={css.emptyS}>Tus pedidos a crédito y facturas aparecerán aquí cuando la empresa los registre.</div>
    </div>
  );
}

function MCard({ icon, label, val, color }) {
  return (
    <div style={{ flex: '1 1 0', minWidth: 90, padding: '14px 12px', borderRadius: 16, background: 'var(--color-surface)', border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color, opacity: 0.7 }}>{icon}</span>
        <span style={{ fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</span>
      </div>
      <span style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{val}</span>
    </div>
  );
}

function Label({ children, style: extra }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', padding: '16px 16px 8px', textTransform: 'uppercase', letterSpacing: '0.5px', ...extra }}>{children}</div>;
}

function Badge({ children, color, bg }) {
  return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: bg, color }}>{children}</span>;
}

function Pill({ icon, label, val, color, accent }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 20, background: accent ? `${accent}14` : 'var(--color-surface)', border: `1px solid ${accent ? `${accent}30` : 'var(--color-border)'}`, fontSize: 11, flexShrink: 0 }}>
      <span style={{ color: accent || 'var(--color-text-secondary)' }}>{icon}</span>
      <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>{label}</span>
      <span style={{ color: accent || 'var(--color-primary)', fontWeight: 600 }}>{val}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   STYLES
   ══════════════════════════════════════════════════════════════════ */
const css = {
  wrap: { display: 'flex', flexDirection: 'column', background: 'var(--color-bg)', minHeight: '100%' },
  cent: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 },
  scroll: {},
  bb: { width: 36, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--color-bg)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 },
  emptyIc: { width: 64, height: 64, borderRadius: '50%', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyT: { fontSize: 16, fontWeight: 600, color: 'var(--color-text)' },
  emptyS: { fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 280 },

  // Hero
  hero: {
    margin: '12px 12px 4px', padding: '20px 18px', borderRadius: 20,
    background: 'linear-gradient(135deg, var(--color-secondary) 0%, var(--color-primary) 100%)',
    color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', position: 'relative', overflow: 'hidden',
  },
  heroTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  heroIc: { width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  heroTitle: { fontSize: 15, fontWeight: 700, color: '#fff' },
  heroSub: { fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 },
  heroVencido: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.18)', fontSize: 11, fontWeight: 700 },
  heroAmountWrap: { marginBottom: 16 },
  heroAmountLabel: { fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  heroAmount: { fontSize: 34, fontWeight: 800, lineHeight: 1.1, display: 'block', marginTop: 4 },
  heroVencidoMonto: { fontSize: 12, color: 'rgba(255,255,255,0.85)', display: 'block', marginTop: 6 },
  heroSkeleton: { display: 'block', height: 30, width: 150, borderRadius: 8, marginTop: 8, background: 'rgba(255,255,255,0.25)', animation: 'agxShimmer 1.2s ease infinite' },
  heroStats: { display: 'flex', gap: 8, borderTop: '1px solid rgba(255,255,255,0.18)', paddingTop: 14 },
  heroStat: { flex: 1, minWidth: 0 },
  heroStatLabel: { fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 500, display: 'block' },
  heroStatVal: { fontSize: 15, fontWeight: 700, color: '#fff', display: 'block', marginTop: 2 },

  summary: { display: 'flex', gap: 8, padding: '12px 12px 8px', flexWrap: 'wrap' },

  // Filtro de historial
  filtroWrap: { display: 'flex', gap: 6, padding: '4px 12px 0', flexWrap: 'wrap' },
  filtroBtn: { flex: 1, minWidth: 90, padding: '9px 12px', borderRadius: 12, border: '1px solid var(--color-border)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s ease' },

  // Timeline
  tl: { display: 'flex', gap: 10, paddingLeft: 4 },
  tlDot: { width: 10, height: 10, borderRadius: '50%', marginTop: 14, flexShrink: 0, boxShadow: '0 0 0 3px var(--color-bg)' },
  tlCard: { flex: 1, minWidth: 0, padding: 14, marginBottom: 10, borderRadius: 16, background: 'var(--color-surface)', border: '1px solid var(--color-border)', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  tlTitle: { fontSize: 15, fontWeight: 700, color: 'var(--color-primary)' },

  // Factura detalle
  factGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8, marginBottom: 16 },
  factCell: { padding: '12px 14px', borderRadius: 12, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column', gap: 3 },
  factLbl: { fontSize: 9, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  factVal: { fontSize: 13, fontWeight: 600, color: 'var(--color-text)' },
  factMontos: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  factMonto: { flex: '1 1 0', minWidth: 100, padding: '14px 12px', borderRadius: 14, background: 'var(--color-surface)', border: '1px solid var(--color-border)', display: 'flex', flexDirection: 'column', gap: 4 },
  factMontoL: { fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 500 },
  factMontoV: { fontSize: 18, fontWeight: 700, color: 'var(--color-primary)' },

  // Detail / Resumen
  cardRow: { display: 'flex', gap: 8, padding: '0 16px 12px', flexWrap: 'wrap' },
  dCard: { flex: '1 1 0', minWidth: 90, padding: '14px 12px', borderRadius: 14, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 4 },
  dCardL: { fontSize: 10, color: 'var(--color-text-muted)', fontWeight: 500 },
  dCardV: { fontSize: 22, fontWeight: 700 },
  sectionH: { fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', padding: '4px 16px 8px', letterSpacing: '0.5px' },
  footBtn: { padding: '14px', borderRadius: 14, border: 'none', cursor: 'pointer', background: 'var(--color-success)', color: '#fff', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },

  btnPri: { flex: 1, padding: '14px', borderRadius: 14, border: 'none', cursor: 'pointer', background: 'var(--color-success)', color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  btnSec: { flex: 1, padding: '14px', borderRadius: 14, border: '1px solid var(--color-border)', cursor: 'pointer', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14, fontWeight: 600 },
  inpLabel: { fontSize: 12, fontWeight: 700, color: 'var(--color-text)', marginBottom: 6 },

  // Desktop modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: '100%', maxWidth: 520, maxHeight: '85vh', background: 'var(--color-surface)', borderRadius: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--color-border)' },
  closeBtn: { width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' },

  // Mobile fullscreen
  fsWrap: { position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' },
  fsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', minHeight: 52 },

  // Product popup
  popOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  popCard: { width: '100%', maxWidth: 360, maxHeight: '85vh', overflowY: 'auto', padding: 20, borderRadius: 20, background: 'var(--color-surface)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};
